import React from 'react';
import { Avatar, Box, Divider, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { cmToInFt, kgToLbs, toCapitalizeFromUnderScore } from '../../lib/helpers/helpers';
import PatientDietQualityReport from '../../screens/PatientsScreen/components/PatientDietQualityReport';
import { ContentCopy, Person } from '@mui/icons-material';
import { getHealthGoals } from '../../lib/helpers/dietIdHelpers';
import NewProgramInfoCard from '../../screens/OrderManagementScreen/components/NewProgramInfoCard';

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  patientName: {
    margin: 0,
    color: theme.palette.text.primary,
    fontWeight: 700,
    lineHeight: '133.4%',
  },
  avatar: {
    height: 40,
    width: 40,
    alignSelf: 'center',
    backgroundColor: theme.palette.admin.avatarBackground,
  },
  copyLinkWrapper: {
    paddingTop: 8,
    paddingBottom: 8,
    paddingRight: 11,
    marginLeft: '40px',
    maxWidth: '90%',
  },
  copyLinkButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
  },
  copyIcon: {
    color: theme.palette.primary.main,
    height: 24,
    width: 24,
    lineHeight: '26px',
    cursor: 'pointer',
  },
  copyText: {
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontSize: 15,
    fontWeight: 500,
    lineHeight: '26px',
    cursor: 'pointer',
  },
  goalsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
    width: '100%',
    overflow: 'hidden',
    '& .MuiChip-root': {
      maxWidth: '180px',
      alignSelf: 'flex-start',
      overflow: 'hidden',
      margin: '0 4px 4px 0',
      '& .MuiChip-label': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  },
  chipWrapper: {
    maxWidth: '140px',
    display: 'inline-block',
  },
  label: {
    color: theme.palette.black60,
    fontSize: '16px',
    lineHeight: '20px',
    letterSpacing: '0.15px',
  },
}));

// Bio Item component for displaying label-value pairs
const BioItem = ({ label, value, noData, minWidth = '50px' }) => {
  const classes = useStyles();

  return (
    <Box
      sx={{
        flex: { xs: '1 1 calc(50% - 6px)', sm: '1 1 calc(50% - 6px)' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '6px',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography
          className={classes.label}
          sx={{
            minWidth: minWidth,
            display: 'inline',
            lineHeight: '1.5',
            margin: 0,
          }}
        >
          {label}:
        </Typography>
        <Typography
          sx={{
            color: '#1C124D',
            minWidth: 0,
            flexShrink: 1,
            display: 'inline',
            lineHeight: '1.5',
            margin: 0,
          }}
        >
          {value || noData}
        </Typography>
      </Box>
    </Box>
  );
};

const BioBox = ({
  bioData,
  assessmentData,
  showAvatar = false,
  firstName,
  lastName,
  showCopyLink = false,
  onCopyLink,
  showProgramInfo = false,
  programInfo,
  isProvider = false,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const noData = 'N/A';

  // Helper function to get data with fallbacks
  const getData = (key, options = {}) => {
    const { transform, fallback, fallbackKey, fallbackTransform } = options;

    if (!bioData) return null;

    if (bioData[key]) {
      return transform ? transform(bioData[key]) : bioData[key];
    }

    if (fallbackKey && bioData[fallbackKey]) {
      return fallbackTransform ? fallbackTransform(bioData[fallbackKey]) : bioData[fallbackKey];
    }

    return fallback || null;
  };

  // Prepare bio item data
  const bioItems = [
    {
      label: 'Gender',
      value: getData('gender', { transform: toCapitalizeFromUnderScore }),
      minWidth: '50px',
    },
    {
      label: 'Age',
      value: getData('ageInYears'),
      minWidth: '50px',
    },
    {
      label: 'Weight (lbs)',
      value: getData('weightInPounds', {
        fallbackKey: 'weightKg',
        fallbackTransform: kgToLbs,
      }),
      minWidth: '90px',
    },
    {
      label: 'Height (in)',
      value: getData('heightInInches', {
        fallbackKey: 'heightCm',
        fallbackTransform: cmToInFt,
      }),
      minWidth: '90px',
    },
  ];

  return (
    <Grid
      item
      xs={12}
      className={classes.grow}
      sx={{
        border: `1px solid ${theme.palette.black12}`,
        borderRadius: 2,
        width: '100%',
        minWidth: '360px',
        maxWidth: '100%',
        padding: 3,
        boxSizing: 'border-box',
      }}
    >
      <Grid
        style={{
          width: '100%',
          display: 'block',
          boxSizing: 'border-box',
        }}
        item
        xs={12}
        container
        justifyContent={'space-around'}
      >
        <>
          {/* Avatar and Name */}
          {showAvatar && (
            <Box className={classes.nameContainer}>
              <Avatar className={classes.avatar}>
                {firstName && lastName ? (
                  toCapitalizeFromUnderScore(firstName)[0] + toCapitalizeFromUnderScore(lastName)[0]
                ) : (
                  <Person />
                )}
              </Avatar>
              <Typography
                variant="h5"
                className={classes.patientName}
              >
                {firstName && lastName
                  ? toCapitalizeFromUnderScore(lastName) + ', ' + toCapitalizeFromUnderScore(firstName)
                  : ''}
              </Typography>
            </Box>
          )}

          {/* Copy Link Button */}
          {showCopyLink && (
            <Grid
              container
              className={classes.copyLinkWrapper}
            >
              <Grid item>
                <Box
                  className={classes.copyLinkButton}
                  onClick={onCopyLink}
                >
                  <ContentCopy className={classes.copyIcon} />
                  <Typography className={classes.copyText}>Copy Sharable Link</Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Gender, Age */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: '12px',
              width: '100%',
            }}
          >
            <BioItem
              label={bioItems[0].label}
              value={bioItems[0].value}
              noData={noData}
              minWidth={bioItems[0].minWidth}
            />
            <BioItem
              label={bioItems[1].label}
              value={bioItems[1].value}
              noData={noData}
              minWidth={bioItems[1].minWidth}
            />
          </Box>

          {/* Weight, Height */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: '12px',
              width: '100%',
              mt: '12px',
            }}
          >
            <BioItem
              label={bioItems[2].label}
              value={bioItems[2].value}
              noData={noData}
              minWidth={bioItems[2].minWidth}
            />
            <BioItem
              label={bioItems[3].label}
              value={bioItems[3].value}
              noData={noData}
              minWidth={bioItems[3].minWidth}
            />
          </Box>

          {/* Goals/Conditions */}
          <Grid
            item
            sx={{
              marginTop: '12px',
              width: '100%',
              maxWidth: { xs: '360px', md: '100%' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'nowrap',
                gap: 1.5,
              }}
            >
              <Typography
                className={classes.label}
                sx={{
                  minWidth: '140px',
                  display: 'inline',
                  lineHeight: '1.5',
                  margin: 0,
                }}
              >
                Goals/Conditions:
              </Typography>
              {!bioData?.lastWorkflow?.raw_responses?.ideal_goals && (
                <Typography
                  sx={{
                    color: '#1C124D',
                    display: 'inline',
                    lineHeight: '1.5',
                    margin: 0,
                  }}
                >
                  {noData}
                </Typography>
              )}
            </Box>

            {bioData?.lastWorkflow?.raw_responses?.ideal_goals && (
              <Box className={classes.goalsContainer}>
                {getHealthGoals(bioData?.lastWorkflow?.raw_responses?.ideal_goals, 'chip')}
              </Box>
            )}
          </Grid>

          {/* Divider between components */}
          <Divider sx={{ my: 3 }} />

          {/* Diet Quality Report */}
          {bioData && assessmentData && (
            <PatientDietQualityReport
              data={{
                ...bioData,
                assessmentData: assessmentData,
                preAndPostQuality:
                  assessmentData.preAndPost || (Array.isArray(assessmentData) && assessmentData[0]?.preAndPost),
                isProvider,
              }}
            />
          )}

          {/* Program Info */}
          {showProgramInfo && programInfo && (
            <Box sx={{ mt: 1 }}>
              <NewProgramInfoCard
                programInfo={programInfo}
                providerView={isProvider}
              />
            </Box>
          )}
        </>
      </Grid>
    </Grid>
  );
};

export default BioBox;
